import { LegalPage } from "@/components/LegalPage";

const SEO = {
    metaTitle: "Privacy Policy | EasiLinked",
    metaDescription:
        "How EasiLinked collects, uses, and protects information on easilinked.com, including our contact form, analytics, and service providers.",
    canonicalUrl: "https://easilinked.com/privacy-policy",
};

const sections = [
    {
        id: "who-we-are",
        heading: "Who we are",
        body: (
            <p>
                {"easilinked.com is operated by EasiLinked LLC (“EasiLinked,” “we,” “us”). If you have questions about this policy or your information, email "}
                <a href="mailto:info@easilinked.com">info@easilinked.com</a>
                {" or call (818) 217-0528."}
            </p>
        ),
    },
    {
        id: "what-we-collect",
        heading: "What we collect",
        body: (
            <>
                <p>We keep data collection to what the website needs to work and to respond to you.</p>
                <ul>
                    <li>
                        <strong>Contact form.</strong>
                        {" When you send us a message, we receive your name, work email, company (optional), and message. We use it only to respond to your inquiry and to follow up on the conversation you started."}
                    </li>
                    <li>
                        <strong>Website analytics.</strong>
                        {" We use Google Analytics, loaded through Google Tag Manager, to understand how the site is used: pages viewed, approximate location, device and browser type, and how visitors arrived. Google Analytics sets first-party cookies (such as _ga) to distinguish visits."}
                    </li>
                    <li>
                        <strong>Technical data.</strong>
                        {" Like any website, our hosting provider processes your IP address, browser details, and the pages you request in order to deliver and protect the site. Contact-form requests are rate-limited by IP address to prevent abuse, and our hosting logs are kept for no more than seven days."}
                    </li>
                    <li>
                        <strong>Theme preference.</strong>
                        {" If you switch between light and dark mode, your choice is saved in your own browser. It is not sent to us."}
                    </li>
                </ul>
                <p>
                    <strong>Please do not send sensitive information</strong>
                    {" (such as patient health information, passwords, or financial account details) through the contact form. If you need to share sensitive information, contact us first and we will arrange a secure method."}
                </p>
            </>
        ),
    },
    {
        id: "domain-scanner",
        heading: "Domain Security Health Check (EasyDMARC)",
        body: (
            <p>
                {"Our homepage offers a free domain scanner provided by our partner EasyDMARC. It only loads if you click “Scan My Domain.” When it loads, the domain you enter is sent to EasyDMARC, and EasyDMARC’s scanner uses its own cookies and analytics and advertising tools, which are governed by "}
                <a href="https://easydmarc.com/legal/privacy-policy/us" target="_blank" rel="noopener noreferrer">
                    {"EasyDMARC’s privacy policy"}
                </a>
                {". If you prefer not to use the scanner, contact us and we will review your domain for you."}
            </p>
        ),
    },
    {
        id: "service-providers",
        heading: "Service providers we use",
        body: (
            <>
                <p>We work with a small number of providers that process information on our behalf:</p>
                <ul>
                    <li>
                        <strong>Cloudflare</strong>
                        {" — website hosting, security, and processing of contact-form submissions."}
                    </li>
                    <li>
                        <strong>Resend</strong>
                        {" — delivers contact-form messages to our inbox."}
                    </li>
                    <li>
                        <strong>Google</strong>
                        {" — Google Workspace (our business email, where inquiries are received) and Google Analytics / Google Tag Manager (website analytics)."}
                    </li>
                    <li>
                        <strong>Sanity</strong>
                        {" — manages our website content. It does not receive the information you submit through the contact form."}
                    </li>
                    <li>
                        <strong>EasyDMARC</strong>
                        {" — only if you choose to use the domain scanner."}
                    </li>
                </ul>
                <p>
                    We do not sell your personal information, and our pages do not run advertising or retargeting
                    pixels.
                </p>
            </>
        ),
    },
    {
        id: "cookies",
        heading: "Cookies and your choices",
        body: (
            <p>
                {"The only cookies our own pages set are Google Analytics cookies. You can block or delete cookies in your browser settings, or opt out of Google Analytics with "}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                    {"Google’s opt-out browser add-on"}
                </a>
                {". EasyDMARC’s cookies are only set if you load the domain scanner."}
            </p>
        ),
    },
    {
        id: "retention",
        heading: "How long we keep information",
        body: (
            <p>
                Inquiries you send us are kept in our business email for as long as needed to respond, maintain our
                business relationship, and meet record-keeping obligations. Analytics data is kept according to our
                Google Analytics retention settings. Hosting logs are kept for no more than seven days.
            </p>
        ),
    },
    {
        id: "your-rights",
        heading: "Your rights",
        body: (
            <p>
                {"You can ask us what information we hold about you, and ask us to correct or delete it, by emailing "}
                <a href="mailto:info@easilinked.com">info@easilinked.com</a>
                {". Depending on where you live, including California, you may have additional rights under applicable privacy laws. We will not treat you differently for exercising them."}
            </p>
        ),
    },
    {
        id: "security",
        heading: "How we protect information",
        body: (
            <p>
                This site is served only over encrypted HTTPS connections and uses strict security headers that limit
                which services can run on our pages. Access to the systems that receive your inquiries is limited to
                EasiLinked personnel who need it.
            </p>
        ),
    },
    {
        id: "children",
        heading: "Children",
        body: <p>This website is intended for businesses and is not directed to children under 13.</p>,
    },
    {
        id: "changes",
        heading: "Changes to this policy",
        body: (
            <p>
                If we change how we handle information, we will update this page and the date at the top.
            </p>
        ),
    },
];

export default function PrivacyPolicyPage() {
    return (
        <LegalPage
            seo={SEO}
            eyebrow="Legal"
            title="Privacy Policy"
            updated="October 27, 2026"
            intro="This policy explains what information easilinked.com collects, why we collect it, and the choices you have. We wrote it in plain language because trust is the foundation of everything we do."
            sections={sections}
        />
    );
}
